import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  StackDivider,
  Badge,
  Spinner,
  Text,
  Box,
  Flex,
  TableContainer,
  IconButton,
  Tooltip,
  HStack
} from '@chakra-ui/react';
import { MdRefresh } from 'react-icons/md';
import Layout from './layout';
import { OUTBOUND_CALLING_DASHBOARD_STRINGS, TAB_STATES } from '@views/OutboundCallingDashboard/constant';
import { fetchAvailableAgents } from '@store/actions/outboundCallingActions';
import CopyableText from '@components/CopyableText';
import { formatDate } from '@utils/dateUtils';

const AgentsDashboard = () => {
  const dispatch = useDispatch();
  const CONSTS = OUTBOUND_CALLING_DASHBOARD_STRINGS.STRING_CONSTANTS[TAB_STATES.ADMIN];

  // Redux state
  const { data: agentsData, isLoading, error } = useSelector(
    (state) => state.outboundCalling?.adminDashboard?.availableAgents || {
      data: null,
      isLoading: false,
      error: null
    }
  );

  const agents = agentsData?.agents || [];

  /**
   * Fetch agents data
   */
  const fetchAgents = useCallback(() => {
    dispatch(fetchAvailableAgents());
  }, [dispatch]);

  /**
   * Fetch on mount
   */
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  /**
   * Get status badge color
   */
  const getStatusColor = (status) => {
    return status?.toLowerCase() === 'active' ? 'green' : 'red';
  };

  /**
   * Render loading state
   */
  const renderLoadingState = () => (
    <Flex justify="center" align="center" w="full" h="200px">
      <VStack spacing={3}>
        <Spinner thickness="4px" size="lg" color="colorPrimary" />
        <Text fontSize="sm" color="whiteAlpha.600">
          {CONSTS.LOADING_AGENTS}
        </Text>
      </VStack>
    </Flex>
  );

  /**
   * Render error state
   */
  const renderErrorState = () => (
    <Box
      p={8}
      bg="backGround"
      border="1px solid"
      borderColor="red.500"
      borderRadius="xl"
      textAlign="center"
    >
      <Text color="red.400" fontSize="lg" fontWeight="bold">
        {CONSTS.ERROR_LOADING_AGENTS}
      </Text>
      <Text color="whiteAlpha.500" fontSize="sm" mt={2}>
        {error || CONSTS.NO_AGENTS_MESSAGE}
      </Text>
    </Box>
  );

  /**
   * Render empty state
   */
  const renderEmptyState = () => (
    <Box
      p={8}
      bg="backGround"
      border="1px solid"
      borderColor="whiteAlpha.300"
      borderRadius="xl"
      textAlign="center"
    >
      <Text color="whiteAlpha.700" fontSize="lg">
        {CONSTS.NO_AGENTS_FOUND}
      </Text>
      <Text color="whiteAlpha.500" fontSize="sm" mt={2}>
        {CONSTS.NO_AGENTS_MESSAGE}
      </Text>
    </Box>
  );

  /**
   * Render agents table
   */
  const renderTable = () => (
    <TableContainer w="100%">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr
            bg="backGround"
            borderBottom="2px solid"
            borderColor="whiteAlpha.500"
          >
            <Th color="whiteAlpha.800">{CONSTS.AGENT_ID}</Th>
            <Th color="whiteAlpha.800">{CONSTS.AGENT_NAME}</Th>
            <Th color="whiteAlpha.800">{CONSTS.AGENT_STATUS}</Th>
            <Th color="whiteAlpha.800">{CONSTS.LAST_PING_AT}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {agents.map((agent) => (
            <Tr
              key={agent.agentId}
              _hover={{ bg: 'whiteAlpha.50' }}
              borderBottom="1px solid"
              borderColor="whiteAlpha.200"
            >
              <Td>
                <CopyableText title={CONSTS.AGENT_ID} color="whiteAlpha.700" fontFamily="mono" fontSize="xs">
                  {agent.agentId}
                </CopyableText>
              </Td>
              <Td color="whiteAlpha.900" fontWeight="medium">
                {agent.agentName || agent.agentId}
              </Td>
              <Td>
                <Badge
                  colorScheme={getStatusColor(agent.status)}
                  fontSize="xs"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {agent.status || CONSTS.INACTIVE}
                </Badge>
              </Td>
              <Td color="whiteAlpha.700">
                {agent.lastPingAt ? formatDate(agent.lastPingAt) : CONSTS.NA}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );

  return (
    <Layout>
      <VStack
        overflowY="initial"
        overflowX="hidden"
        alignItems="stretch"
        gap={4}
        px={4}
        pt={4}
        w="100%"
        divider={<StackDivider borderColor="whiteAlpha.300" />}
      >
        <HStack justify="space-between" align="center">
          <Heading size="lg" color="colorPrimary">
            {CONSTS.AGENTS_DASHBOARD}
          </Heading>
          <Tooltip label={CONSTS.REFRESH_AGENTS || 'Refresh agents list'} placement="left">
            <IconButton
              icon={<MdRefresh />}
              onClick={fetchAgents}
              isLoading={isLoading}
              colorScheme="green"
              variant="outline"
              size="md"
              aria-label="Refresh agents"
              color="colorPrimary"
              borderColor="colorPrimary"
              _hover={{ bg: 'whiteAlpha.100' }}
            />
          </Tooltip>
        </HStack>

        {isLoading && renderLoadingState()}
        {error && !isLoading && renderErrorState()}
        {!isLoading && !error && agents.length === 0 && renderEmptyState()}
        {!isLoading && !error && agents.length > 0 && renderTable()}
      </VStack>
    </Layout>
  );
};

export default React.memo(AgentsDashboard);